<template>
  <SubscriptionNavigation
    @submit-clicked="onSubmit"
    :is-submitting-data="isSaveSubscriptionInProgress"
    :is-loading-data="isGetSubscriptionInProgress"
    :submit-label="t('common.startSubscription')"
  >
    <div data-cy="pdl-registration-page">
      <div class="text-center mt-4">
        <AtomText
          :text="t('subscription.pdl.title')"
          font-style="font-semibold"
          font-size="text-2xl"
          data-cy="pdl-registration-page-title"
        />
        <AtomText class="mt-2" :content-html="t('subscription.pdl.description')" />
        <div class="mt-2 flex items-center justify-center gap-3">
          <MoleculeTag
            :value="t('subscription.pdl.tags.time')"
            variant="icon"
            type="secondary"
            icon-name="ClockIcon"
            size="lg"
          />
          <MoleculeTag
            :value="t('subscription.pdl.tags.withoutEngagement')"
            type="secondary"
            variant="icon"
            icon-name="LockOpenIcon"
            size="lg"
          />
        </div>
      </div>
      <AtomDivider class="my-8" />

      <AtomText
        :text="t('subscription.pdl.startSubscription')"
        font-style="font-semibold"
        font-size="text-lg"
        data-cy="pdl-registration-page-start-subscription"
      />
      <AtomText
        :text="t('subscription.pdl.enterPdl')"
        color-variant="description"
        font-size="text-sm"
      />

      <OrganismAlert
        :title="t('subscription.pdl.infoAlert.title')"
        class="my-6"
        data-cy="pdl-alert-info"
        hasIcon
        state="info"
      />

      <OrganismAlert
        v-if="displayAlertError"
        variant="complex"
        :title="t('subscription.pdl.errorAlert.technicalError.title')"
        :content-html="t('subscription.pdl.errorAlert.technicalError.description')"
        class="mb-6"
        data-cy="pdl-alert-error"
        state="error"
      />

      <div data-cy="mlh-pdl-input">
        <MoleculeLabel
          :text="t('subscription.pdl.input.label')"
          class="mt-6 mb-1"
          data-cy="pdl-input-label"
          label-for="pdl"
          mode-helper="tooltip"
          helper-button-aria-label="hover over to display a tooltip"
        >
          <template #tooltip-content-slot>
            <p v-html="t('subscription.pdl.input.tooltip')"></p>
          </template>
        </MoleculeLabel>

        <MoleculeInputText
          id="pdl"
          v-model="pdl"
          data-cy="pdl-input"
          :placeholder="t('subscription.pdl.input.placeholder')"
          mask="frenchEan"
          :helper="pdlHelperMessage"
          :input-state="pdlInputState"
          :disabled="isSaveSubscriptionInProgress || isGetSubscriptionInProgress"
          @keydown="onKeyDownPdlInput"
          @blur="onBlurPdlInput"
          class="w-full"
          name="pdl"
        />
      </div>
    </div>
  </SubscriptionNavigation>
</template>

<script lang="ts" setup>
import { isAxiosError } from 'axios';
import { storeToRefs } from 'pinia';
import { useField } from 'vee-validate';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import * as yup from 'yup';

import { useStepStore, useSubscriptionStore, useToastSnackbarStore } from '@/stores';
import { ToastType } from '@/stores/toastSnackbar.store';

import type { PdlErrorType } from '@/types/errors.type';
import { getErrorToasterConfig } from '@/utils/error.util';
import { pdlRegex } from '@/utils/regex.util';

import SubscriptionNavigation from '@/components/SubscriptionNavigation.component.vue';

import { EPdlErrorType } from '@/constants/errors.contants';

const { t } = useI18n();

const toastSnackBar = useToastSnackbarStore();
const subscriptionStore = useSubscriptionStore();
const stepStore = useStepStore();

const { goToNextStep } = stepStore;

const {
  pdlNumber,
  subscriptionIdFromStorage,
  isGetSubscriptionSuccessfully,
  isSaveSubscriptionInProgress,
  isGetSubscriptionInProgress,
  getCurrentSubscriptionId,
  getCurrentSubscriptionPdl,
} = storeToRefs(subscriptionStore);

const { setIsResubscription } = subscriptionStore;

const {
  value: pdl,
  errors: pdlFormatErrors,
  errorMessage: pdlFormatError,
  validate: validatePdl,
} = useField<string>(
  'pdl',
  yup
    .string()
    .required(t('common.forms.required'))
    .matches(pdlRegex, t('subscription.pdl.input.helper.format')),
  {
    initialValue: getCurrentSubscriptionPdl.value,
  }
);

const isUnknownPdl = ref(false);
const isUnavailablePdl = ref(false);
const isTechnicalError = ref(false);

const isSubmitted = ref(false);
const hasBlurPdlInput = ref(false);

const isSubmittedOrHasBlurPdlInput = computed(() => isSubmitted.value || hasBlurPdlInput.value);

const displayAlertError = computed(() => {
  return isSubmitted.value && isTechnicalError.value;
});

const pdlHelperMessage = computed(() => {
  const helper = {
    success: '',
    error: '',
    info: '',
  };

  if (isSubmittedOrHasBlurPdlInput.value) {
    if (pdlFormatError.value && pdlFormatError.value !== '') {
      helper.error = pdlFormatError.value;
    }

    if (isUnknownPdl.value) {
      helper.error = t('subscription.pdl.input.helper.unknownPdl');
    }

    if (isUnavailablePdl.value) {
      helper.error = t('subscription.pdl.input.helper.unavailablePdl');
    }
  }

  return helper;
});

const pdlInputState = computed(() => {
  if (
    isSubmittedOrHasBlurPdlInput.value &&
    (pdlFormatErrors.value.length > 0 || isUnknownPdl.value || isUnavailablePdl.value)
  ) {
    return 'error';
  }
  return 'info';
});

const onBlurPdlInput = () => {
  hasBlurPdlInput.value = true;
};

const resetPdlErrorStatus = () => {
  hasBlurPdlInput.value = false;
  isUnknownPdl.value = false;
  isUnavailablePdl.value = false;
  isTechnicalError.value = false;
};

const resetSubmit = () => {
  isSubmitted.value = false;
};

const onKeyDownPdlInput = () => {
  resetPdlErrorStatus();
  resetSubmit();
};

const onSubmit = async () => {
  resetPdlErrorStatus();

  isSubmitted.value = true;

  toastSnackBar.clearToasts();

  const pdlValidation = await validatePdl();

  if (!pdlValidation.valid) {
    return;
  }

  if (pdl.value === getCurrentSubscriptionPdl.value) {
    goToNextStep();
    return;
  }

  try {
    const submitSubscription = getCurrentSubscriptionId.value
      ? subscriptionStore.updateSubscriptionWithPdl
      : subscriptionStore.createSubscriptionWithPdl;

    await submitSubscription(pdl.value);
    setIsResubscription(false);
    goToNextStep();
  } catch (error: unknown) {
    handlePdlSubmissionError(error);
  }
};

const handlePdlSubmissionError = (error: unknown) => {
  if (!isAxiosError(error)) {
    toastSnackBar.addToast(
      getErrorToasterConfig({
        error,
        toasterId: 'pdl-error',
        defaultMessage: t('subscription.pdl.toast.default'),
      })
    );
    return;
  }

  const status = error.response?.status;
  const detail = error.response?.data?.detail;

  if (status === 422) {
    handle422Error(detail as PdlErrorType);
  } else if (status === 500) {
    isTechnicalError.value = true;
    toastSnackBar.addToast({
      id: 'error500',
      title: t('subscription.pdl.toast.500.title'),
      content: t('subscription.pdl.toast.500.description'),
      timer: 6000,
      toastType: ToastType.Error,
    });
  } else {
    toastSnackBar.addToast(
      getErrorToasterConfig({
        error,
        toasterId: 'pdl-error',
        defaultMessage: t('subscription.pdl.toast.default'),
      })
    );
  }
};

const handle422Error = (detail: PdlErrorType) => {
  switch (detail) {
    case EPdlErrorType.PDL_UNAVAILABLE:
      isUnavailablePdl.value = true;
      break;
    case EPdlErrorType.PDL_NOT_FOUND:
      isUnknownPdl.value = true;
      break;
    case EPdlErrorType.CONTRACT_NUMBER_MANDATORY:
      subscriptionStore.setIsResubscription(true);
      subscriptionStore.setPdl(pdl.value);
      goToNextStep();
      break;
  }
};

watch(isGetSubscriptionSuccessfully, isSuccessfully => {
  if (isSuccessfully) {
    pdl.value = getCurrentSubscriptionPdl.value;
  }
});

onMounted(() => {
  if (!subscriptionIdFromStorage.value) {
    pdl.value = pdlNumber.value;
  }
});
</script>
